/**
 * PIN / token display + clipboard helpers.
 *
 * Used by every wallet surface that shows a PIN, voucher code, meter token, or
 * cash-withdrawal credential to a user. Display is always grouped in 4-digit
 * chunks; clipboard copy is always the raw value with no whitespace.
 */

/**
 * Group a PIN/token's DIGITS into chunks of `groupSize` separated by single
 * spaces, for display only. Non-digit characters are stripped before grouping.
 * Returns the raw input unchanged if it contains no digits.
 *
 *   formatPinForDisplay('1234567891234567')  -> '1234 5678 9123 4567'
 *   formatPinForDisplay('123456789123')      -> '1234 5678 9123'
 *   formatPinForDisplay('91234567891234')    -> '9123 4567 8912 34'
 *   formatPinForDisplay('VOUCHER-XYZ')       -> 'VOUCHER-XYZ'  (no digits)
 *   formatPinForDisplay('')                  -> ''
 */
export function formatPinForDisplay(pin: string | null | undefined, groupSize = 4): string {
  if (!pin) return '';
  const raw = String(pin);
  const digits = raw.replace(/\D/g, '');
  if (!digits) return raw;
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += groupSize) {
    groups.push(digits.slice(i, i + groupSize));
  }
  return groups.join(' ');
}

/**
 * Return the PIN value to write to the clipboard: the original string with ALL
 * whitespace removed. Digit-only PINs come out as raw digits; non-digit tokens
 * keep their characters. This is the canonical "copy as one string, no spaces".
 *
 *   pinToClipboardValue('1234 5678 9123 4567') -> '1234567891234567'
 *   pinToClipboardValue('1234567891234567')    -> '1234567891234567'
 *   pinToClipboardValue('9 1234 5678 9123 4')  -> '91234567891234'
 *   pinToClipboardValue('')                    -> ''
 */
export function pinToClipboardValue(pin: string | null | undefined): string {
  if (!pin) return '';
  return String(pin).replace(/\s+/g, '');
}
